/**
 * WeaponSystem - Manages helicopter weapons and firing
 * Handles M230 Chain Gun, Hydra 70 Rockets, and AGM-114 Hellfire missiles
 */

export enum WeaponType {
  CHAIN_GUN = 'M230_CHAIN_GUN',
  ROCKETS = 'HYDRA_70',
  HELLFIRE = 'AGM_114_HELLFIRE',
}

export interface Weapon {
  type: WeaponType;
  name: string;
  ammo: number;
  maxAmmo: number;
  fireRate: number; // rounds per second
  lastFireTime: number;
}

export interface Target {
  id: string;
  position: { x: number; y: number; z: number };
  designated: boolean;
  lockTime: number;
}

export interface Projectile {
  id: string;
  type: WeaponType;
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  targetId?: string;
  active: boolean;
}

export class WeaponSystem {
  private weapons: Map<WeaponType, Weapon>;
  private currentWeapon: WeaponType;
  private targets: Map<string, Target>;
  private projectiles: Projectile[];
  private nextProjectileId: number = 0;

  constructor() {
    this.weapons = new Map([
      [
        WeaponType.CHAIN_GUN,
        {
          type: WeaponType.CHAIN_GUN,
          name: 'M230 30mm',
          ammo: 1200,
          maxAmmo: 1200,
          fireRate: 10, // 10 rounds per second (600 RPM)
          lastFireTime: 0,
        },
      ],
      [
        WeaponType.ROCKETS,
        {
          type: WeaponType.ROCKETS,
          name: 'Hydra 70',
          ammo: 76,
          maxAmmo: 76,
          fireRate: 2, // 2 rockets per second
          lastFireTime: 0,
        },
      ],
      [
        WeaponType.HELLFIRE,
        {
          type: WeaponType.HELLFIRE,
          name: 'AGM-114',
          ammo: 16,
          maxAmmo: 16,
          fireRate: 0.5, // 1 missile every 2 seconds
          lastFireTime: 0,
        },
      ],
    ]);

    this.currentWeapon = WeaponType.CHAIN_GUN;
    this.targets = new Map();
    this.projectiles = [];
  }

  /**
   * Cycle to next weapon
   */
  cycleWeapon(): void {
    const types = [WeaponType.CHAIN_GUN, WeaponType.ROCKETS, WeaponType.HELLFIRE];
    const currentIndex = types.indexOf(this.currentWeapon);
    const nextIndex = (currentIndex + 1) % types.length;
    this.currentWeapon = types[nextIndex];
  }

  /**
   * Get current weapon
   */
  getCurrentWeapon(): Weapon {
    return this.weapons.get(this.currentWeapon)!;
  }

  /**
   * Fire current weapon
   */
  fire(
    currentTime: number,
    helicopterPos: { x: number; y: number; z: number },
    helicopterRot: { pitch: number; roll: number; yaw: number },
    aimDirection?: { x: number; y: number; z: number }
  ): Projectile | null {
    const weapon = this.getCurrentWeapon();

    // Check ammo
    if (weapon.ammo <= 0) {
      return null;
    }

    // Check fire rate
    const timeSinceLastFire = currentTime - weapon.lastFireTime;
    const minTimeBetweenShots = 1000 / weapon.fireRate; // Convert to milliseconds

    if (timeSinceLastFire < minTimeBetweenShots) {
      return null;
    }

    // Create projectile based on weapon type
    let projectile: Projectile | null = null;

    switch (weapon.type) {
      case WeaponType.CHAIN_GUN:
        projectile = this.fireChainGun(helicopterPos, aimDirection);
        break;
      case WeaponType.ROCKETS:
        projectile = this.fireRocket(helicopterPos, helicopterRot);
        break;
      case WeaponType.HELLFIRE:
        projectile = this.fireHellfire(helicopterPos, helicopterRot);
        break;
    }

    if (projectile) {
      weapon.ammo--;
      weapon.lastFireTime = currentTime;
      this.projectiles.push(projectile);
    }

    return projectile;
  }

  private fireChainGun(
    helicopterPos: { x: number; y: number; z: number },
    aimDirection?: { x: number; y: number; z: number }
  ): Projectile {
    // Chain gun fires in direction of aim (turret independent)
    const dir = aimDirection || { x: 0, y: 0, z: -1 };
    const speed = 800; // m/s muzzle velocity

    return {
      id: `projectile_${this.nextProjectileId++}`,
      type: WeaponType.CHAIN_GUN,
      position: { ...helicopterPos },
      velocity: {
        x: dir.x * speed,
        y: dir.y * speed,
        z: dir.z * speed,
      },
      active: true,
    };
  }

  private fireRocket(
    helicopterPos: { x: number; y: number; z: number },
    helicopterRot: { pitch: number; roll: number; yaw: number }
  ): Projectile {
    // Rockets fire in helicopter's forward direction
    const speed = 700; // m/s
    const forward = this.getForwardVector(helicopterRot);

    return {
      id: `projectile_${this.nextProjectileId++}`,
      type: WeaponType.ROCKETS,
      position: { ...helicopterPos },
      velocity: {
        x: forward.x * speed,
        y: forward.y * speed,
        z: forward.z * speed,
      },
      active: true,
    };
  }

  private fireHellfire(
    helicopterPos: { x: number; y: number; z: number },
    helicopterRot: { pitch: number; roll: number; yaw: number }
  ): Projectile {
    // Hellfire can be fired without a designated target (will follow unguided)
    const designatedTarget = Array.from(this.targets.values()).find((t) => t.designated);

    const speed = 450; // m/s
    const forward = this.getForwardVector(helicopterRot);

    // LOAL (Lock-On After Launch) - missile initially goes up
    return {
      id: `projectile_${this.nextProjectileId++}`,
      type: WeaponType.HELLFIRE,
      position: { ...helicopterPos },
      velocity: {
        x: forward.x * speed * 0.5,
        y: speed * 0.8, // Initial upward trajectory
        z: forward.z * speed * 0.5,
      },
      targetId: designatedTarget?.id,
      active: true,
    };
  }

  private getForwardVector(rotation: { pitch: number; roll: number; yaw: number }): {
    x: number;
    y: number;
    z: number;
  } {
    // Calculate forward vector from rotation
    const x = Math.sin(rotation.yaw) * Math.cos(rotation.pitch);
    const y = Math.sin(rotation.pitch);
    const z = -Math.cos(rotation.yaw) * Math.cos(rotation.pitch);

    return { x, y, z };
  }

  /**
   * Designate a target for laser-guided weapons
   */
  designateTarget(targetId: string): void {
    // Clear previous designation
    this.targets.forEach((target) => {
      target.designated = false;
    });

    const target = this.targets.get(targetId);
    if (target) {
      target.designated = true;
      target.lockTime = Date.now();
    }
  }

  /**
   * Add a target to the system
   */
  addTarget(target: Target): void {
    this.targets.set(target.id, target);
  }

  /**
   * Remove a target
   */
  removeTarget(targetId: string): void {
    this.targets.delete(targetId);
  }

  /**
   * Get all targets
   */
  getTargets(): Target[] {
    return Array.from(this.targets.values());
  }

  /**
   * Get designated target
   */
  getDesignatedTarget(): Target | undefined {
    return Array.from(this.targets.values()).find((t) => t.designated);
  }

  /**
   * Update projectile positions
   */
  updateProjectiles(deltaTime: number): void {
    this.projectiles.forEach((projectile) => {
      if (!projectile.active) return;

      // Update position
      projectile.position.x += projectile.velocity.x * deltaTime;
      projectile.position.y += projectile.velocity.y * deltaTime;
      projectile.position.z += projectile.velocity.z * deltaTime;

      // Apply gravity to rockets and hellfires
      if (projectile.type !== WeaponType.CHAIN_GUN) {
        projectile.velocity.y -= 9.81 * deltaTime;
      }

      // Hellfire guidance
      if (projectile.type === WeaponType.HELLFIRE && projectile.targetId) {
        const target = this.targets.get(projectile.targetId);
        if (target) {
          this.guideHellfireToTarget(projectile, target, deltaTime);
        }
      }

      // Deactivate projectiles that hit ground or go too far
      if (projectile.position.y < 0) {
        projectile.active = false;
      }

      const distanceFromOrigin = Math.sqrt(
        projectile.position.x ** 2 + projectile.position.y ** 2 + projectile.position.z ** 2
      );
      if (distanceFromOrigin > 10000) {
        projectile.active = false;
      }
    });

    // Remove inactive projectiles
    this.projectiles = this.projectiles.filter((p) => p.active);
  }

  private guideHellfireToTarget(projectile: Projectile, target: Target, deltaTime: number): void {
    // Calculate direction to target
    const dx = target.position.x - projectile.position.x;
    const dy = target.position.y - projectile.position.y;
    const dz = target.position.z - projectile.position.z;

    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (distance < 1) return;

    // Normalize direction
    const dirX = dx / distance;
    const dirY = dy / distance;
    const dirZ = dz / distance;

    // Adjust velocity towards target (proportional guidance)
    const guidanceStrength = 2.0;
    projectile.velocity.x += dirX * guidanceStrength * 100 * deltaTime;
    projectile.velocity.y += dirY * guidanceStrength * 100 * deltaTime;
    projectile.velocity.z += dirZ * guidanceStrength * 100 * deltaTime;

    // Maintain roughly constant speed
    const speed = Math.sqrt(
      projectile.velocity.x ** 2 + projectile.velocity.y ** 2 + projectile.velocity.z ** 2
    );
    const targetSpeed = 450;
    const speedFactor = targetSpeed / speed;

    projectile.velocity.x *= speedFactor;
    projectile.velocity.y *= speedFactor;
    projectile.velocity.z *= speedFactor;
  }

  /**
   * Get all active projectiles
   */
  getProjectiles(): Projectile[] {
    return this.projectiles.filter((p) => p.active);
  }

  /**
   * Get weapon status for HUD
   */
  getWeaponStatus(): {
    currentWeapon: string;
    ammo: number;
    maxAmmo: number;
  } {
    const weapon = this.getCurrentWeapon();
    return {
      currentWeapon: weapon.name,
      ammo: weapon.ammo,
      maxAmmo: weapon.maxAmmo,
    };
  }
}
